import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../../../services/baseQuery';
import type { ApiResponse } from '../../menu/types';
import { toCamelCase } from '../../grid/utils';
import type { MoMSalesReportOptions, MoMSalesReportRequest, MoMSalesReportResult } from '../types';

const MOM_SALES_REPORT_API = '/umbraco/api/MoMSalesReport';

type ErrorPayload = { Error?: string };

const transformResponse = <T>(response: ApiResponse<unknown>): T => {
  if ((response.Code ?? response.code) !== 1) {
    const payload = response.data as ErrorPayload | undefined;
    throw new Error(payload?.Error || 'Failed to load MoM sales report.');
  }

  return toCamelCase(response.data ?? {}) as T;
};

export const transformMomSalesOptionsResponse = (
  response: ApiResponse<unknown>
): MoMSalesReportOptions => transformResponse<MoMSalesReportOptions>(response);

export const transformMoMSalesReportResponse = (
  response: ApiResponse<unknown>
): MoMSalesReportResult => transformResponse<MoMSalesReportResult>(response);

export const momSalesReportApi = createApi({
  reducerPath: 'momSalesReportApi',
  baseQuery,
  endpoints: (builder) => ({
    getOptions: builder.query<MoMSalesReportOptions, void>({
      query: () => `${MOM_SALES_REPORT_API}/GetOptions`,
      transformResponse: transformMomSalesOptionsResponse,
      keepUnusedDataFor: 300,
    }),
    generateReport: builder.query<MoMSalesReportResult, MoMSalesReportRequest>({
      query: (body) => ({
        url: `${MOM_SALES_REPORT_API}/GenerateGridReport`,
        method: 'POST',
        body,
      }),
      transformResponse: transformMoMSalesReportResponse,
    }),
  }),
});

export const { useGetOptionsQuery, useLazyGenerateReportQuery } = momSalesReportApi;
